import { defineStore } from 'pinia';
import type { SuggestionGeometry } from '@/components/place-suggestions/geometry';

// Bridge between the Place Suggestions panel (Scout sidebar) and the Trackers
// map. Each suggestion card registers its polygon here while mounted; the
// DeviceMap watches the registry and draws a numbered pin per entry. Clicking a
// card sets `focusedId`, which the map reads to recenter + highlight that pin.

export type SuggestionMapEntry = {
  id: string;
  rank: number;
  geometry: SuggestionGeometry;
};

type State = {
  entries: SuggestionMapEntry[];
  focusedId: string | null;
};

export const usePlaceSuggestionsMapStore = defineStore('placeSuggestionsMap', {
  state: (): State => ({
    entries: [],
    focusedId: null,
  }),
  getters: {
    /** True while at least one suggestion is being rendered on the map. */
    isActive: (state): boolean => state.entries.length > 0,
  },
  actions: {
    /**
     * Add or replace a suggestion entry. Cards call this once their polygon
     * loads and again whenever their rank shifts (e.g. after a sibling is
     * dismissed). Entries stay sorted by rank so pin numbers match the panel.
     */
    register(entry: SuggestionMapEntry) {
      const index = this.entries.findIndex((e) => e.id === entry.id);
      if (index >= 0) {
        this.entries.splice(index, 1, entry);
      } else {
        this.entries.push(entry);
      }
      this.entries.sort((a, b) => a.rank - b.rank);
    },
    /** Remove a suggestion entry (card unmounted, dismissed, or panel closed). */
    unregister(id: string) {
      this.entries = this.entries.filter((e) => e.id !== id);
      if (this.focusedId === id) {
        this.focusedId = null;
      }
    },
    /** Mark a suggestion as the focused one so the map recenters on it. */
    focus(id: string) {
      this.focusedId = id;
    },
    /** Drop all entries and focus — used when the suggestions panel closes. */
    clear() {
      this.entries = [];
      this.focusedId = null;
    },
  },
});
